import { createEmptyMarketQuery, normalizeMarketQuery } from './marketQuery';

const BOOLEAN_MAPS = Object.freeze({
  profitability_ranges: { M: 'under_35', A: '35_75', B: '75_150', C: '150_400', D: 'over_400' },
  entry_types: { per: 'PER', 0: 'RR', 1: 'RRA', 2: 'LT', 3: 'LTA', 6: 'SE', 7: 'MKT' },
  value_types: { Stock: 'stock', ETF: 'etf' },
  dividends: { 0: '0', 1: '1', 2: '2', 3: '3_plus' }
});

const BOOLEAN_KEY_ORDER = Object.freeze({
  profitability_ranges: ['M', 'A', 'B', 'C', 'D'],
  entry_types: ['per', '0', '1', '2', '3', '6', '7'],
  value_types: ['Stock', 'ETF'],
  dividends: ['0', '1', '2', '3']
});

function selectedKeys(source, map, keyOrder, omitAll = false) {
  const selected = keyOrder.filter(key => source && source[key]).map(key => map[key]);
  return omitAll && selected.length === Object.keys(map).length ? [] : selected;
}

function addRange(filters, name, minimum, maximum, options = {}) {
  const range = {};
  if (minimum !== undefined && minimum !== null && minimum !== '' && minimum != options.neutralMin) range.min = minimum;
  if (maximum !== undefined && maximum !== null && maximum !== '' && maximum != options.neutralMax) range.max = maximum;
  if (Object.keys(range).length) filters[name] = range;
}

function toIsoDate(value) {
  if (!value || /^\d{4}-\d{2}-\d{2}$/.test(value)) return value || '';
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
}

function toLegacyDate(value) {
  if (!value || /^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value || '';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
}

function addDateRange(filters, name, from, to) {
  const range = {};
  if (from) range.from = toIsoDate(from);
  if (to) range.to = toIsoDate(to);
  if (Object.keys(range).length) filters[name] = range;
}

export function legacyFinderToMarketQuery(finder = {}, baseQuery = createEmptyMarketQuery()) {
  const query = normalizeMarketQuery(baseQuery);
  const filters = {};
  const strings = [
    ['currency', 'currencies'], ['country', 'countries'],
    ['sector', 'sectors'], ['industry', 'industries']
  ];
  strings.forEach(([legacy, canonical]) => {
    const values = Array.isArray(finder[legacy]) ? finder[legacy].filter(value => value && value !== '0') : [];
    if (values.length) filters[canonical] = values;
  });

  const directions = [];
  if (finder.direccio && finder.direccio[0]) directions.push(0);
  if (finder.direccio && finder.direccio[1]) directions.push(1);
  if (directions.length && directions.length !== 2) filters.directions = directions;

  Object.keys(BOOLEAN_MAPS).forEach(name => {
    const legacyName = {
      profitability_ranges: 'tae_checkboxes', entry_types: 'entry_type',
      value_types: 'value_type', dividends: 'dividends'
    }[name];
    const selected = selectedKeys(finder[legacyName], BOOLEAN_MAPS[name], BOOLEAN_KEY_ORDER[name], name !== 'entry_types');
    if (selected.length) filters[name] = selected;
  });

  addRange(filters, 'amount', finder.min_amount, finder.max_amount, { neutralMin: 0, neutralMax: 99999999999999 });
  addRange(filters, 'entry_distance', finder.min_entry_distance, finder.max_entry_distance, { neutralMin: 0, neutralMax: 25 });
  addRange(filters, 'age', finder.min_edad, finder.max_edad);
  addRange(filters, 'long_term_profitability', finder.min_tae_lt, finder.max_tae_lt);
  addRange(filters, 'wave', finder.min_ona, finder.max_ona);
  addRange(filters, 'annual_variation', finder.min_anual_variation, finder.max_anual_variation);
  addDateRange(filters, 'support_date', finder.min_support_date, finder.max_support_date);
  addDateRange(filters, 'profitability_date', finder.profit_date && finder.profit_date[0], finder.profit_date && finder.profit_date[1]);
  addDateRange(filters, 'max_min_bar_date', finder.max_min_bar_date && finder.max_min_bar_date[0], finder.max_min_bar_date && finder.max_min_bar_date[1]);

  if (finder.es_ipo && finder.es_ipo[0] && !finder.es_ipo[1]) filters.regular_values_only = true;
  if (String(finder.extrad_cfds_direction) === '1') filters.extra_cfds_direction = true;

  return normalizeMarketQuery({ ...query, filters, pagination: { ...query.pagination, offset: 0 } });
}

function booleanObject(selected, map) {
  return Object.keys(map).reduce((result, key) => {
    result[key] = selected.indexOf(map[key]) !== -1;
    return result;
  }, {});
}

export function marketQueryToLegacyFinder(query, template = {}) {
  const normalized = normalizeMarketQuery(query);
  const filters = normalized.filters;
  const finder = JSON.parse(JSON.stringify(template || {}));
  finder.currency = filters.currencies ? [...filters.currencies] : [];
  finder.country = filters.countries ? [...filters.countries] : [];
  finder.sector = filters.sectors ? [...filters.sectors] : [];
  finder.industry = filters.industries ? [...filters.industries] : [];
  finder.direccio = [
    !filters.directions || filters.directions.indexOf(0) !== -1,
    !filters.directions || filters.directions.indexOf(1) !== -1
  ];
  finder.tae_checkboxes = booleanObject(filters.profitability_ranges || [], BOOLEAN_MAPS.profitability_ranges);
  finder.entry_type = booleanObject(filters.entry_types || [], BOOLEAN_MAPS.entry_types);
  finder.value_type = booleanObject(filters.value_types || [], BOOLEAN_MAPS.value_types);
  finder.dividends = Object.values(booleanObject(filters.dividends || [], BOOLEAN_MAPS.dividends));
  finder.es_ipo = [Boolean(filters.regular_values_only), false];
  finder.extrad_cfds_direction = filters.extra_cfds_direction ? '1' : '0';

  const numeric = [
    ['amount', 'min_amount', 'max_amount', 0, 99999999999999],
    ['entry_distance', 'min_entry_distance', 'max_entry_distance', 0, 25],
    ['age', 'min_edad', 'max_edad', '', ''],
    ['long_term_profitability', 'min_tae_lt', 'max_tae_lt', '', ''],
    ['wave', 'min_ona', 'max_ona', '', ''],
    ['annual_variation', 'min_anual_variation', 'max_anual_variation', '', '']
  ];
  numeric.forEach(([name, minKey, maxKey, defaultMin, defaultMax]) => {
    finder[minKey] = filters[name] && filters[name].min !== undefined ? filters[name].min : defaultMin;
    finder[maxKey] = filters[name] && filters[name].max !== undefined ? filters[name].max : defaultMax;
  });

  finder.min_support_date = filters.support_date ? toLegacyDate(filters.support_date.from) : '';
  finder.max_support_date = filters.support_date ? toLegacyDate(filters.support_date.to) : '';
  finder.profit_date = filters.profitability_date
    ? [toLegacyDate(filters.profitability_date.from), toLegacyDate(filters.profitability_date.to)] : ['', ''];
  finder.max_min_bar_date = filters.max_min_bar_date
    ? [toLegacyDate(filters.max_min_bar_date.from), toLegacyDate(filters.max_min_bar_date.to)] : ['', ''];
  return finder;
}
